import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCompanionStore } from '../../store/companionStore';
import CompanionChat from './CompanionChat';
import CompanionTranscript from './CompanionTranscript';
import { RecapPlayerContainer } from '../Recap';
import styles from './CompanionDock.module.css';

/**
 * CompanionDock — the persistent, platform-wide AI-tutor surface.
 *
 * This is the "spine" from docs/ide-ux-spec.md: one agent present on EVERY
 * screen (mounted in Layout), so the relationship follows the student across
 * the dashboard, course pages, the library, and the lab IDE — not just inside
 * a lab. Collapsed it's a small launcher; expanded it's a side panel.
 *
 * Scope of this slice (v1): the ubiquitous, accessible dock chrome only. The
 * body is an honest placeholder describing the Companion's role; the chat,
 * longitudinal memory, struggle-detection, and routing to Co-Pilot/Forge are
 * later slices. On the lab route the existing TeachingAgent/TeachingSidebar
 * still renders in-page; consolidating that surface into this dock is a
 * follow-up (tracked against the spec), which is why this slice is additive
 * and non-breaking.
 *
 * Accessibility (spec §12 — WCAG/EAA is a launch gate, not polish):
 *  - launcher is a real <button> with aria-expanded/aria-controls;
 *  - opening moves focus into the panel; Escape / Close returns it to the
 *    launcher so keyboard users are never dropped at the top of the page;
 *  - while closed the panel is `inert`, removing it from the tab order and the
 *    accessibility tree (set imperatively to stay type-safe under React 18);
 *  - motion respects prefers-reduced-motion (handled in the stylesheet).
 */
const CompanionDock: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isOpen = useCompanionStore((s) => s.isOpen);
  const open = useCompanionStore((s) => s.open);
  const toggle = useCompanionStore((s) => s.toggle);
  const close = useCompanionStore((s) => s.close);
  // Transcript tab appears only when a lecture has published its transcript.
  const hasTranscript = useCompanionStore((s) => !!s.lectureContext);
  const [tab, setTab] = useState<'chat' | 'transcript'>('chat');
  // MV5 Sprint 2.B — the AI tutor writes `recapSuggestion` when the model
  // emits a `recap_available` marker. The dock renders the RecapPlayer chip
  // above the chat tab so the learner can hit Play without leaving the tutor
  // context. Cleared by the setLectureContext transition (companionStore).
  const recapSuggestion = useCompanionStore((s) => s.recapSuggestion);
  const clearRecapSuggestion = useCompanionStore((s) => s.setRecapSuggestion);

  // The lab IDE needs the space, but route changes must not override a
  // learner's explicit collapsed/open preference.
  const isIDE =
    location.pathname.startsWith('/lab/') ||
    location.pathname.startsWith('/environment/');
  const preferredOpenRef = useRef(isOpen);
  const previousIsIDERef = useRef<boolean | null>(null);
  useEffect(() => {
    const previous = previousIsIDERef.current;
    if (isIDE && previous !== true) {
      preferredOpenRef.current = isOpen;
      close();
    } else if (!isIDE && previous === true && preferredOpenRef.current) {
      open();
    }
    previousIsIDERef.current = isIDE;
  }, [isIDE, isOpen, open, close]);

  const launcherRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const panelId = useId();

  // Mount the chat lazily on first open and keep it mounted thereafter, so the
  // tutor session persists across collapses and route changes (the dock lives
  // in Layout and never unmounts) without eagerly creating a session — and
  // hitting the teaching backend — for users who never open it.
  const [hasOpened, setHasOpened] = useState(false);

  // Toggle `inert` on the panel imperatively: React 18's JSX types don't carry
  // the `inert` attribute, and setting it via a ref also dodges the empty-
  // string-vs-boolean serialization quirk. `inert` pulls the whole panel
  // (incl. the Close button) out of the tab order and a11y tree when hidden,
  // which is the correct way to hide an off-canvas panel — aria-hidden alone
  // would leave the Close button focusable.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (isOpen) {
      el.removeAttribute('inert');
      setHasOpened(true);
      // Land focus on Close — the first interactive control — rather than
      // leaving it on the (now-hidden) launcher.
      closeRef.current?.focus();
    } else {
      el.setAttribute('inert', '');
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    preferredOpenRef.current = false;
    close();
    launcherRef.current?.focus();
  }, [close]);

  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    },
    [handleClose],
  );

  return (
    <>
      {/* Launcher — hidden while open; the panel's Close button takes over. */}
      <button
        ref={launcherRef}
        type="button"
        className={styles.launcher}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={t('companion.open_label')}
        onClick={() => {
          preferredOpenRef.current = !isOpen;
          toggle();
        }}
        hidden={isOpen}
      >
        <span aria-hidden="true" className={styles.launcherGlyph}>
          🎓
        </span>
        <span className={styles.launcherText}>{t('companion.name')}</span>
      </button>

      <aside
        ref={panelRef}
        id={panelId}
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        role="complementary"
        aria-label={t('companion.name')}
        onKeyDown={handlePanelKeyDown}
      >
        <header className={styles.panelHeader}>
          <span className={styles.panelTitle}>
            <span aria-hidden="true">🎓</span> {t('companion.name')}
          </span>
          <button
            ref={closeRef}
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t('common.close')}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        <div className={styles.body}>
          {/* On a lecture, the transcript folds in here as a tab instead of a
              third column beside the video. Tabs appear only then. */}
          {hasTranscript && (
            <div className={styles.tabs} role="tablist" aria-label={t('companion.name')}>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'chat'}
                className={tab === 'chat' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => setTab('chat')}
              >
                Chat
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'transcript'}
                className={tab === 'transcript' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => setTab('transcript')}
              >
                Transcript
              </button>
            </div>
          )}

          {/* Recap suggestion — rendered as the RecapPlayer V1 chip above the
              chat when the AI tutor's last turn emitted a `recap_available`
              marker. Container owns the V1→V5→V3→V4 state machine and the
              backend calls. */}
          {recapSuggestion && (
            <div className={styles.recapSlot}>
              <RecapPlayerContainer
                lectureId={recapSuggestion.lectureId}
                lectureTitle={recapSuggestion.lectureTitle}
                suggestedSegmentIds={recapSuggestion.suggestedSegmentIds}
                onDismiss={() => clearRecapSuggestion(null)}
              />
            </div>
          )}

          {/* Chat stays mounted (so the tutor session survives tab/route
              changes) but is hidden behind the transcript tab. */}
          <div className={styles.tabPanel} hidden={hasTranscript && tab === 'transcript'}>
            {hasOpened && <CompanionChat />}
          </div>
          {hasTranscript && tab === 'transcript' && <CompanionTranscript />}
        </div>
      </aside>
    </>
  );
};

export default CompanionDock;
